import { DiscordSDK } from '@discord/embedded-app-sdk';
import { api } from '../lib/api';
import type { DiscordContext, SpoylUser } from '../types';

/**
 * Discord injects `frame_id` in the query string of the iframe it opens.
 * Its absence means we are running in a plain browser tab (local preview).
 */
export function isEmbedded(): boolean {
  return new URLSearchParams(window.location.search).has('frame_id');
}

const CLIENT_ID = import.meta.env.VITE_DISCORD_CLIENT_ID as string | undefined;

/**
 * Scopes requested at boot, kept to exactly what the backend calls:
 * `identify` for `/users/@me`, `guilds` for `/users/@me/guilds` — the latter
 * carries the `owner` flag, which is how the owner gets the console.
 *
 * `guilds.members.read` used to be here and nothing ever read it. An unused
 * scope is free reach for a leaked token, and it widens the set Discord has to
 * find already-granted before it can authorize silently. Add a scope back the
 * day something actually calls it.
 */
const SCOPES = ['identify', 'guilds'] as const;

export interface AuthenticatedSession {
  user: SpoylUser;
  context: DiscordContext;
  accessToken: string;
}

let sdkInstance: DiscordSDK | null = null;

export function getDiscordSdk(): DiscordSDK {
  if (!sdkInstance) {
    if (!CLIENT_ID) {
      throw new Error(
        'VITE_DISCORD_CLIENT_ID is not set. Copy activity/client/.env.example to .env.',
      );
    }
    sdkInstance = new DiscordSDK(CLIENT_ID);
  }
  return sdkInstance;
}

/** The one handshake in flight (or already done) for this page load. */
let handshake: Promise<AuthenticatedSession> | null = null;

/**
 * Full handshake, run **at most once per page load**.
 *
 * `authorize` is not idempotent: every call makes Discord open and close its
 * authorization flow, which the user sees as the client flashing and chiming.
 * React's StrictMode deliberately mounts, unmounts and remounts every component
 * in development, so the boot effect fires twice — and a `cancelled` flag in the
 * effect's cleanup only suppresses the state updates, it cannot recall a request
 * already sent to Discord. Sharing one promise is what actually stops the second
 * handshake, and it holds for any other double-invoke too.
 *
 * A failure is not cached, so a retry can still succeed.
 */
export function authenticateWithDiscord(): Promise<AuthenticatedSession> {
  handshake ??= runHandshake().catch((cause: unknown) => {
    handshake = null;
    throw cause;
  });
  return handshake;
}

/**
 * Gets an OAuth2 authorization code, silently when it can be.
 *
 * `authorize` opens Discord's consent modal whenever the user doesn't already
 * hold a valid token for *every* scope asked for. Left to itself that modal
 * opens and dismisses on each launch, which is what the client shows as a flash
 * and a pair of open/close chimes.
 *
 * `prompt: 'none'` is the standard OAuth2/OIDC way to say "reuse the grant this
 * user already gave, and fail rather than ask again". It does not skip consent —
 * it skips *re-asking*. When there is nothing to reuse (a first launch, or a
 * scope we didn't request last time) it rejects, and the interactive call below
 * is the only path forward. That is a feature: a scope added later can never be
 * granted silently, the user is always shown the new one.
 *
 * The catch is deliberately broad. Any silent attempt that fails, for whatever
 * reason, simply falls back to the behaviour we had before — never to no
 * authorization at all.
 */
async function requestAuthorizationCode(sdk: DiscordSDK): Promise<string> {
  // Spread into each call rather than shared by reference: `authorize` takes a
  // mutable scope array, so each attempt gets its own copy.
  const request = {
    client_id: sdk.clientId,
    response_type: 'code' as const,
    state: '',
  };

  try {
    const silent = await sdk.commands.authorize({
      ...request,
      scope: [...SCOPES],
      prompt: 'none',
    });
    return silent.code;
  } catch {
    const consented = await sdk.commands.authorize({ ...request, scope: [...SCOPES] });
    return consented.code;
  }
}

/**
 * Wait for the host, get an OAuth2 code, exchange it on our backend, then hand
 * the access token back to Discord to open the RPC session.
 */
async function runHandshake(): Promise<AuthenticatedSession> {
  const sdk = getDiscordSdk();

  await sdk.ready();

  const code = await requestAuthorizationCode(sdk);

  const { access_token: accessToken } = await api<{ access_token: string }>('/token', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });

  const auth = await sdk.commands.authenticate({ access_token: accessToken });
  if (!auth) {
    throw new Error('Discord refused the authentication handshake');
  }

  return {
    accessToken,
    user: {
      id: auth.user.id,
      username: auth.user.username,
      displayName: auth.user.global_name ?? auth.user.username,
      avatar: auth.user.avatar ?? null,
    },
    context: {
      guildId: sdk.guildId,
      channelId: sdk.channelId,
      instanceId: sdk.instanceId,
    },
  };
}
